import React, { useContext, useState } from "react";
import { Navigate } from "react-router-dom";
import { Alert, Card, Spinner } from "react-bootstrap";
import {
  IconShieldOff,
  IconUsers,
  IconLock,
  IconClock,
} from "@tabler/icons-react";
import UserContext from "../../../contexts/UserContext";
import Swal from "sweetalert2";

// Utilitário para abrir WhatsApp do suporte
const openSupportWhatsApp = (featureName) => {
  const phoneNumber = "554791360752";
  const message = `Olá! Gostaria de solicitar aumento no limite de uso para a funcionalidade "${featureName}". Poderia me ajudar com isso?`;
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  
  Swal.fire({
    title: 'Aumentar Limite de Uso',
    html: `
      <div style="text-align: center;">
        <p>Para aumentar seu limite de uso da funcionalidade <strong>${featureName}</strong>, entre em contato com nosso suporte.</p>
        <div style="margin: 20px 0;">
          <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" 
               alt="WhatsApp" 
               style="width: 40px; height: 40px; margin-bottom: 10px;">
        </div>
        <p style="color: #666; font-size: 14px;">Clique no botão abaixo para abrir o WhatsApp com uma mensagem pré-formatada.</p>
      </div>
    `,
    icon: 'info',
    showCancelButton: true,
    confirmButtonText: '📱 Abrir WhatsApp',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#25D366',
    cancelButtonColor: '#6c757d',
    customClass: {
      confirmButton: 'btn btn-success',
      cancelButton: 'btn btn-secondary'
    }
  }).then((result) => {
    if (result.isConfirmed) {
      // Abrir WhatsApp em nova aba
      window.open(whatsappUrl, '_blank');
      
      // Mostrar feedback de confirmação
      Swal.fire({
        title: 'WhatsApp Aberto!',
        text: 'O WhatsApp foi aberto em uma nova aba. Nossa equipe responderá em breve!',
        icon: 'success',
        timer: 3000,
        showConfirmButton: false
      });
    }
  });
};

// Utilitário para gerenciar preferências de notificação
const UsageLimitPreferences = {
  // Limpar todas as preferências de dismissal para uma feature quando limite for resolvido
  clearDismissalsForFeature: (featureId) => {
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(`usage-limit-dismissed-${featureId}-`)
      );
      keys.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Erro ao limpar preferências:', error);
    }
  },
  
  // Verificar se uma notificação específica foi fechada
  isDismissed: (featureId, currentUsage) => {
    try {
      const key = `usage-limit-dismissed-${featureId || 'unknown'}-${currentUsage || 0}`;
      return localStorage.getItem(key) === 'true';
    } catch (error) {
      return false;
    }
  },
  
  // Marcar uma notificação como fechada
  setDismissed: (featureId, currentUsage) => {
    try {
      const key = `usage-limit-dismissed-${featureId || 'unknown'}-${currentUsage || 0}`;
      localStorage.setItem(key, 'true');
    } catch (error) {
      console.warn('Não foi possível salvar preferência:', error);
    }
  }
};

// Componente de notificação não-intrusiva para limites de uso
const UsageLimitNotification = ({ feature, usage, onUpgradeClick, onDismiss }) => {
  return (
    <Alert variant="warning" className="mb-3 border-0 shadow-sm" dismissible onClose={onDismiss}>
      <div className="d-flex align-items-center">
        <IconClock size={20} className="text-warning me-2" />
        <div className="flex-grow-1">
          <div className="d-flex align-items-center mb-1 gap-2">
            <small><strong>Limite de uso atingido para {feature?.name}</strong></small>
            <span className="badge bg-warning text-dark">
              {usage?.current_usage}/{usage?.usage_limit}
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">Algumas funcionalidades estão limitadas</small>
            <button 
              className="btn btn-sm btn-warning" 
              onClick={onUpgradeClick}
              style={{ fontSize: '12px', padding: '2px 8px' }}
            >
              Aumentar Limite
            </button>
          </div>
        </div>
      </div>
    </Alert>
  );
};

// Componente wrapper para acesso limitado
const LimitedAccessWrapper = ({ children, feature, usage, limitedActions }) => {
  // Verificar se o usuário já fechou esta notificação para este estado específico
  const [notificationVisible, setNotificationVisible] = useState(() => {
    return !UsageLimitPreferences.isDismissed(feature?.id, usage?.current_usage);
  });
  
  const handleUpgradeClick = () => {
    openSupportWhatsApp(feature?.name || 'esta funcionalidade');
  };

  const handleDismiss = () => {
    UsageLimitPreferences.setDismissed(feature?.id, usage?.current_usage);
    setNotificationVisible(false);
  };

  return (
    <>
      {/* Alert compacto não-intrusivo */}
      {notificationVisible && (
        <UsageLimitNotification 
          feature={feature}
          usage={usage}
          onUpgradeClick={handleUpgradeClick}
          onDismiss={handleDismiss}
        />
      )}
      
      {/* Componente com props de limitação */}
      {React.cloneElement(children, {
        isLimitExceeded: true,
        usageInfo: usage,
        feature: feature,
        limitedActions: limitedActions,
        onUpgradeClick: handleUpgradeClick
      })}
    </>
  );
};

// Componente para mostrar diferentes tipos de acesso negado
const AccessDeniedComponent = ({ reason, feature, usage, requiredAction }) => {
  const actionLabels = {
    create: "criar",
    read: "visualizar", 
    update: "editar",
    delete: "excluir",
  };

  const actionIcons = {
    create: "➕",
    read: "👁️",
    update: "✏️", 
    delete: "🗑️",
  };

  // Para limites de uso excedidos, não renderizar nada aqui
  // O componente pai irá tratar esse caso
  if (reason === "usage_exceeded") {
    return null;
  }

  // Erro por feature não encontrada
  if (reason === "feature_not_found") {
    return (
      <div className="container mt-5">
        <Card className="border-danger">
          <Card.Body className="text-center p-5">
            <IconShieldOff size={64} className="text-danger mb-3" />
            <h4 className="text-danger mb-3">Funcionalidade Não Encontrada</h4>
            <p className="text-muted mb-4">
              A funcionalidade solicitada <strong>Feature ID: {feature?.id}</strong> não foi encontrada 
              no seu perfil de permissões.
            </p>

            <div className="row justify-content-center mb-4">
              <div className="col-md-8">
                <Alert variant="danger">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span><strong>Feature ID:</strong></span>
                    <span className="badge bg-danger">
                      {feature?.id || "Não especificado"}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span><strong>Status:</strong></span>
                    <span className="badge bg-warning text-dark">Não encontrada</span>
                  </div>
                </Alert>
              </div>
            </div>

            <p className="text-muted mb-3">
              <strong>💡 Possíveis causas:</strong> Esta funcionalidade pode não estar 
              disponível para o seu tipo de conta ou ainda não ter sido configurada.
            </p>

            {process.env.NODE_ENV === "development" && (
              <Alert variant="light" className="mt-4">
                <small>
                  <strong>🔍 Debug Info:</strong><br />
                  Feature ID procurado: <code>{feature?.id}</code><br />
                  Ação solicitada: <code>{requiredAction}</code><br />
                  Verifique se o ID existe em: <code>src/constants/FeatureIds.js</code>
                </small>
              </Alert>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Erro por falta de permissão
  return (
    <div className="container mt-5">
      <Card className="border-info">
        <Card.Body className="text-center p-5">
          <IconUsers size={64} className="text-info mb-3" />
          <h4 className="text-info mb-3">Permissão Negada</h4>
          <p className="text-muted mb-4">
            Você não tem permissão para{" "}
            <strong>
              {actionIcons[requiredAction]} {actionLabels[requiredAction]}
            </strong>{" "}
            na funcionalidade <strong>{feature?.name || "não identificada"}</strong>.
          </p>

          <div className="row justify-content-center mb-4">
            <div className="col-md-8">
              <Alert variant="info">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span><strong>Funcionalidade:</strong></span>
                  <span className="badge bg-primary">
                    {feature?.name || `Feature ID: ${feature?.id || "Não encontrada"}`}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span><strong>Ação necessária:</strong></span>
                  <span className="badge bg-info">
                    {actionIcons[requiredAction]} {actionLabels[requiredAction]}
                  </span>
                </div>
                {feature?.category && (
                  <div className="d-flex justify-content-between align-items-center">
                    <span><strong>Categoria:</strong></span>
                    <span className="badge bg-secondary">{feature.category}</span>
                  </div>
                )}
              </Alert>
            </div>
          </div>

          <p className="text-muted mb-3">
            <strong>💡 O que fazer:</strong> Entre em contato com seu administrador 
            para solicitar acesso a esta funcionalidade.
          </p>

          {/* Debug info para desenvolvimento */}
          {process.env.NODE_ENV === "development" && (
            <Alert variant="light" className="mt-4">
              <small>
                <strong>🔍 Debug Info:</strong><br />
                Feature ID: <code>{feature?.id}</code><br />
                Slug: <code>{feature?.slug}</code><br />
                Ação: <code>{requiredAction}</code><br />
                Motivo: <code>{reason}</code>
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

const ProtectedRoute = ({
  children,
  requiredPermission = null,
  requiredFeature = null, // NOVO: ID da feature
  requiredFeatureSlug = null, // NOVO: SLUG da feature (preferido sobre ID pra features novas)
  requiredModule = null, // MANTER para compatibilidade temporária
  requiredAction = "read",
  requireSuperuser = false,
  requiredLevel = null,
  fallbackPath = "/login",
  showLoadingForPermissions = false,
  checkUsage = true, // NOVO: verificar limites de uso
}) => {
  const {
    user,
    logged,
    hasPermission,
    hasPermissionBySlug,
    canPerformActionWithUsage,
    getFeatureById,
    permissionsLoading,
    permissionsError,
    isInitializing,
    isSupportSession,
  } = useContext(UserContext);

  const actionLabels = {
    create: "criar",
    read: "visualizar", 
    update: "editar",
    delete: "excluir",
  };

  const actionIcons = {
    create: "➕",
    read: "👁️",
    update: "✏️", 
    delete: "🗑️",
  };


  // ===============================================
  // 1. VERIFICAÇÃO DE INICIALIZAÇÃO (NOVA)
  // ===============================================
  if (isInitializing) {
    return (
      <div className="container mt-5">
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center p-5">
            <IconClock size={64} className="text-primary mb-3" />
            <h5 className="text-primary mb-2">Inicializando Sistema</h5>
            <p className="text-muted mb-3">
              Verificando autenticação e carregando configurações...
            </p>
            <Spinner animation="border" variant="primary" size="sm" />
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 2. VERIFICAÇÃO DE AUTENTICAÇÃO
  // ===============================================
  if (!logged || !user) {
    return <Navigate to={fallbackPath} replace />;
  }

  // ===============================================
  // 3. VERIFICAÇÃO DE LOADING DE PERMISSÕES (OPCIONAL)
  // ===============================================
  if (showLoadingForPermissions && permissionsLoading && requiredModule) {
    return (
      <div className="container mt-5">
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center p-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5 className="text-primary mb-2">Verificando Permissões</h5>
            <p className="text-muted mb-1">
              Carregando suas permissões para o módulo{" "}
              <strong>{requiredModule}</strong>
            </p>
            <small className="text-muted">
              Isso pode levar alguns segundos...
            </small>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 4. TRATAMENTO DE ERRO NAS PERMISSÕES
  // ===============================================
  if (permissionsError && requiredModule && showLoadingForPermissions) {
    return (
      <div className="container mt-5">
        <Card className="border-warning">
          <Card.Body className="text-center p-5">
            <IconShieldOff size={64} className="text-warning mb-3" />
            <h4 className="text-warning mb-3">Erro ao Verificar Permissões</h4>
            <p className="text-muted mb-4">
              Não foi possível carregar suas permissões detalhadas. O sistema
              está usando permissões básicas baseadas no seu nível de usuário.
            </p>
            <Alert variant="warning" className="mb-3">
              <div className="d-flex align-items-center">
                <IconShieldOff size={20} className="me-2" />
                <div>
                  <strong>Erro:</strong>{" "}
                  {permissionsError?.message || "Erro desconhecido"}
                </div>
              </div>
            </Alert>
            <small className="text-muted">
              Se o problema persistir, entre em contato com o administrador.
            </small>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 5. VERIFICAÇÃO DE SUPERUSUÁRIO
  // ===============================================
  if (requireSuperuser && !user.is_superuser && !isSupportSession) {
    return (
      <div className="container mt-5">
        <Card className="border-danger">
          <Card.Body className="text-center p-5">
            <IconShieldOff size={64} className="text-danger mb-3" />
            <h4 className="text-danger mb-3">Acesso Restrito</h4>
            <p className="text-muted mb-4">
              Esta área requer privilégios de <strong>administrador</strong>.
              Somente usuários com permissões especiais podem acessar este
              conteúdo.
            </p>
            <Alert variant="danger">
              <div className="d-flex justify-content-between align-items-center">
                <span>
                  <strong>Seu status atual:</strong>
                </span>
                <span className="badge bg-secondary">
                  {user.is_superuser ? "Superusuário" : "Usuário Comum"}
                </span>
              </div>
            </Alert>
            <small className="text-muted">
              Entre em contato com seu administrador para solicitar acesso.
            </small>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 6. VERIFICAÇÃO DE NÍVEL HIERÁRQUICO
  // ===============================================
  if (requiredLevel !== null && user.superuser_level > requiredLevel && !isSupportSession) {

    const levelNames = {
      1: "Admin",
      2: "Gerente",
      3: "Supervisor",
      4: "Atendente",
    };

    const levelColors = {
      1: "danger",
      2: "warning",
      3: "info",
      4: "secondary",
    };

    return (
      <div className="container mt-5">
        <Card className="border-warning">
          <Card.Body className="text-center p-5">
            <IconLock size={64} className="text-warning mb-3" />
            <h4 className="text-warning mb-3">Nível de Acesso Insuficiente</h4>
            <p className="text-muted mb-4">
              Esta funcionalidade requer nível{" "}
              <strong>{levelNames[requiredLevel]}</strong> ou superior.
            </p>
            <div className="row justify-content-center">
              <div className="col-md-8">
                <Alert variant="warning">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>
                      <strong>Nível necessário:</strong>
                    </span>
                    <span className={`badge bg-${levelColors[requiredLevel]}`}>
                      {levelNames[requiredLevel]} (Nível {requiredLevel})
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      <strong>Seu nível atual:</strong>
                    </span>
                    <span
                      className={`badge bg-${
                        levelColors[user.superuser_level] || "secondary"
                      }`}
                    >
                      {levelNames[user.superuser_level] || "Não definido"}{" "}
                      (Nível {user.superuser_level})
                    </span>
                  </div>
                </Alert>
              </div>
            </div>
            <small className="text-muted">
              Solicite uma promoção ao seu supervisor ou administrador.
            </small>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===============================================
  // 6.5. VERIFICAÇÃO DE PERMISSÃO POR SLUG DE FEATURE (preferido pra features novas)
  // ===============================================
  if (requiredFeatureSlug) {
    if (isSupportSession) {
      return children;
    }

    if (permissionsLoading) {
      return (
        <div className="container mt-5">
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center p-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <h5 className="text-primary mb-2">Verificando Permissões</h5>
              <p className="text-muted mb-0">Aguarde um instante...</p>
            </Card.Body>
          </Card>
        </div>
      );
    }

    if (!hasPermissionBySlug(requiredFeatureSlug, requiredAction)) {
      return (
        <AccessDeniedComponent
          reason="no_permission"
          feature={{ slug: requiredFeatureSlug, name: null, category: null }}
          requiredAction={requiredAction}
        />
      );
    }
  }

  // ===============================================
  // 7. VERIFICAÇÃO DE PERMISSÃO POR ID DE FEATURE (NOVA)
  // ===============================================
  if (requiredFeature) {
    // Sessão de suporte tem acesso total às features
    if (isSupportSession) {
      return children;
    }

    const feature = getFeatureById(requiredFeature);
    // Se feature não foi encontrada e não estamos em loading, verificar se é admin
    if (!feature && !permissionsLoading) {
      // Admin (level 1) sempre tem acesso, mesmo que feature não esteja no perfil
      if (user.superuser_level === 1) {
        // Admin sempre tem acesso, mesmo que feature não esteja no perfil
      } else {

        return <AccessDeniedComponent 
          reason="feature_not_found" 
          feature={{ id: requiredFeature, name: null, category: null }}
          requiredAction={requiredAction}
        />;
      }
    }
    
    // Sempre executar verificação de permissão (hasPermission já trata admin corretamente)
    if (checkUsage) {
      const result = canPerformActionWithUsage(requiredFeature, requiredAction);
      
      if (!result.allowed) {
        // Para limites de uso excedidos, usar degradação progressiva
        if (result.reason === "usage_exceeded") {
          // Permitir acesso read-only, mas injetar informações sobre limite
          if (requiredAction === "read") {
            const featureData = result.feature || feature || { id: requiredFeature, name: null, category: null };
            
            return (
              <LimitedAccessWrapper
                feature={featureData}
                usage={result.usage}
                limitedActions={["create", "update", "delete"]}
              >
                {children}
              </LimitedAccessWrapper>
            );
          }
          
          // Para ações de write, ainda bloquear mas com mensagem melhorada
          return (
            <div className="container mt-5">
              <Card className="border-warning">
                <Card.Body className="text-center p-5">
                  <IconClock size={64} className="text-warning mb-3" />
                  <h4 className="text-warning mb-3">Limite de Uso Excedido</h4>
                  <p className="text-muted mb-4">
                    Você atingiu o limite para <strong>{(result.feature || feature || { name: 'esta funcionalidade' }).name}</strong>.
                    Para {actionLabels[requiredAction] || requiredAction}, entre em contato com seu administrador.
                  </p>

                  <div className="row justify-content-center mb-4">
                    <div className="col-md-8">
                      <Alert variant="warning">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span><strong>Uso atual:</strong></span>
                          <span className="badge bg-warning text-dark">
                            {result.usage?.current_usage}/{result.usage?.usage_limit}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span><strong>Ação solicitada:</strong></span>
                          <span className="badge bg-info">
                            {(actionIcons[requiredAction] || "🔧")} {actionLabels[requiredAction] || requiredAction}
                          </span>
                        </div>
                        {result.usage?.organization_notes && (
                          <div className="mt-3 pt-2 border-top">
                            <small><em>{result.usage.organization_notes}</em></small>
                          </div>
                        )}
                      </Alert>
                    </div>
                  </div>

                  <div className="d-flex justify-content-center gap-3">
                    <button 
                      className="btn btn-warning" 
                      onClick={() => window.history.back()}
                    >
                      Voltar
                    </button>
                    <button 
                      className="btn btn-outline-warning"
                      onClick={() => {
                        openSupportWhatsApp((result.feature || feature || { name: 'esta funcionalidade' }).name);
                      }}
                    >
                      📱 Solicitar Aumento
                    </button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          );
        }

        // Outros tipos de erro mantém comportamento original
        return <AccessDeniedComponent 
          reason={result.reason} 
          feature={result.feature || feature || { id: requiredFeature, name: null, category: null }}
          usage={result.usage}
          requiredAction={requiredAction}
        />;
      }
    } else {
      // Verificação simples sem considerar limites de uso (admin sempre passa aqui)
      if (!hasPermission(requiredFeature, requiredAction)) {

        return <AccessDeniedComponent 
          reason="no_permission" 
          feature={feature || { id: requiredFeature, name: null, category: null }}
          requiredAction={requiredAction}
        />;
      }
    }
  }

  // ===============================================
  // 8. VERIFICAÇÃO DE PERMISSÃO POR MÓDULO (COMPATIBILIDADE REMOVIDA)
  // ===============================================
  else if (requiredModule) {

    // Fallback: Para módulos antigos, sempre permitir para admin (level 1)
    // e negar para outros usuários com aviso para migração
    if (user.superuser_level === 1) {
      // Admin tem acesso a módulos obsoletos
    } else {
      
      return (
        <div className="container mt-5">
          <Card className="border-warning">
            <Card.Body className="text-center p-5">
              <IconUsers size={64} className="text-warning mb-3" />
              <h4 className="text-warning mb-3">Sistema em Atualização</h4>
              <p className="text-muted mb-4">
                Esta funcionalidade está sendo migrada para o novo sistema de permissões.
              </p>

              <div className="row justify-content-center mb-4">
                <div className="col-md-8">
                  <Alert variant="warning">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>
                        <strong>Módulo legado:</strong>
                      </span>
                      <span className="badge bg-warning text-dark">{requiredModule}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>
                        <strong>Status:</strong>
                      </span>
                      <span className="badge bg-info">Em migração</span>
                    </div>
                  </Alert>
                </div>
              </div>

              <p className="text-muted mb-3">
                <strong>💡 O que fazer:</strong> Entre em contato com o administrador 
                para verificar suas permissões no novo sistema.
              </p>

              {/* Debug info para desenvolvedores */}
              {process.env.NODE_ENV === "development" && (
                <Alert variant="light" className="mt-4">
                  <small>
                    <strong>🔍 Para Desenvolvedores:</strong>
                    <br />
                    Módulo obsoleto: <code>{requiredModule}</code>
                    <br />
                    Migre para: <code>requiredFeature={"{ID_DA_FEATURE}"}</code>
                    <br />
                    Consulte o arquivo: <code>src/constants/FeatureIds.js</code>
                  </small>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </div>
      );
    }
  }

  // ===============================================
  // 9. VERIFICAÇÃO DE PERMISSÃO CUSTOMIZADA (FUNÇÃO)
  // ===============================================
  if (requiredPermission && typeof requiredPermission === "function") {
    if (!requiredPermission(user)) {
      return (
        <div className="container mt-5">
          <Card className="border-secondary">
            <Card.Body className="text-center p-5">
              <IconShieldOff size={64} className="text-secondary mb-3" />
              <h4 className="text-secondary mb-3">Acesso Negado</h4>
              <p className="text-muted mb-4">
                Você não atende aos <strong>critérios específicos</strong>{" "}
                necessários para acessar esta área do sistema.
              </p>
              <Alert variant="secondary">
                <strong>Verificação personalizada falhada</strong>
                <br />
                <small>
                  Esta página possui regras de acesso customizadas que não foram
                  atendidas.
                </small>
              </Alert>
              <small className="text-muted">
                Entre em contato com seu administrador para mais informações.
              </small>
            </Card.Body>
          </Card>
        </div>
      );
    }
  }

  // ===============================================
  // 10. ACESSO AUTORIZADO - RENDERIZAR COMPONENTE
  // ===============================================

  return children;
};

// Exportar utilitários para uso em outros componentes
export { UsageLimitPreferences, openSupportWhatsApp };

export default ProtectedRoute;
